import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GraduationCap, Pin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NoteCard = ({ name, note, testid }) => (
  <div className="rounded-xl border border-[#ffab00]/25 bg-[#ffab00]/[0.04] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2 text-[#ffab00] font-tab text-xs uppercase tracking-widest">
      <GraduationCap className="w-4 h-4" /> Teacher's tips{name ? ` · ${name}` : ""}
    </div>
    <p className="text-sm text-slate-300 whitespace-pre-wrap">{note}</p>
  </div>
);

export const TeacherNotes = ({ tab, user, sharedNote, onNoteChange }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pinning, setPinning] = useState(false);

  useEffect(() => {
    if (!onNoteChange) return;
    if (data?.mine) onNoteChange({ name: user?.name, note: data.mine });
    else if (data?.teacher) onNoteChange(data.teacher);
    else onNoteChange(sharedNote || null);
  }, [data, sharedNote, user?.name, onNoteChange]);

  const pinLesson = async () => {
    setPinning(true);
    try {
      await api.post("/team/lesson", { tab });
      toast.success("Pinned as your class's Lesson of the week");
      window.dispatchEvent(new Event("beatflow:lesson-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not pin lesson");
    } finally {
      setPinning(false);
    }
  };

  useEffect(() => {
    if (!user || !tab?.title) { setData(null); return; }
    api.get("/teacher-notes", { params: { title: tab.title, artist: tab.artist || "" } })
      .then((r) => { setData(r.data); setDraft(r.data.mine || ""); setEditing(false); })
      .catch(() => setData(null));
  }, [user, tab?.title, tab?.artist]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/teacher-notes", { title: tab.title, artist: tab.artist || "", note: draft });
      setData((d) => ({ ...d, mine: draft.trim() }));
      setEditing(false);
      toast.success(draft.trim() ? "Teacher notes saved" : "Teacher notes removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save notes");
    } finally {
      setSaving(false);
    }
  };

  const showShared = sharedNote && !(data?.can_write && data?.mine);
  const hasAnything = showShared || data?.teacher || data?.can_write || user?.is_team_owner;
  if (!hasAnything) return null;

  return (
    <div className="px-5 py-4 border-t border-white/10 flex flex-col gap-3" data-testid="teacher-notes">
      {user?.is_team_owner && (
        <button
          onClick={pinLesson}
          disabled={pinning}
          className="self-start inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-tab border border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/5 hover:bg-[#00f0ff]/15 transition-colors disabled:opacity-50"
          data-testid="pin-lesson-button"
        >
          <Pin className="w-3.5 h-3.5" /> {pinning ? "Pinning…" : "Pin as Lesson of the week"}
        </button>
      )}
      {showShared && <NoteCard name={sharedNote.name} note={sharedNote.note} testid="shared-teacher-note" />}
      {data?.teacher && <NoteCard name={data.teacher.name} note={data.teacher.note} testid="team-teacher-note" />}
      {data?.can_write && (editing ? (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-slate-500 font-tab uppercase tracking-widest">Your practice tips for students</span>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="e.g. Start at 70 BPM, keep the hi-hat relaxed, count the fill out loud before bar 8…"
            className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-600"
            data-testid="teacher-notes-textarea"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#ffab00] text-black hover:bg-[#ffc233]" data-testid="teacher-notes-save">
              {saving ? "Saving…" : "Save notes"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(data.mine || ""); setEditing(false); }} data-testid="teacher-notes-cancel">Cancel</Button>
          </div>
          <span className="text-[11px] text-slate-500">Visible to your team students and anyone you share this tab with.</span>
        </div>
      ) : data.mine ? (
        <div className="flex flex-col gap-2">
          <NoteCard name="You" note={data.mine} testid="my-teacher-note" />
          <button onClick={() => setEditing(true)} className="self-start text-xs font-tab text-[#ffab00] hover:underline" data-testid="teacher-notes-edit">Edit notes</button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="self-start inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-tab border border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/5 hover:bg-[#ffab00]/15 transition-colors"
          data-testid="teacher-notes-add"
        >
          <GraduationCap className="w-3.5 h-3.5" /> Add teacher notes
        </button>
      ))}
    </div>
  );
};
